<template>
  <v-card
    v-if="customerGuide"
    border
  >
    <v-dialog
      v-model="isEditingGuide"
      max-width="1100"
      persistent
    >
      <GuideEditor
        v-if="isEditingGuide"
        :guide-material-id="guideMaterialId"
        @close="closeGuideEditor"
        @submitted="handleDraftSubmission"
        @dirty-change="$emit('draft-dirty', $event)"
      />
    </v-dialog>
    <ConfirmationDialog
      ref="regenerationConfirmation"
      :title="$t('landingpage.customer-guide.confirmation.title')"
      :text="$t('landingpage.customer-guide.confirmation.text')"
      :ok-button-text="$t('landingpage.customer-guide.confirmation.confirm')"
    />
    <v-card-title>
      <h3 class="text-overline text-grey text--darken-1">
        {{ $t('landingpage.customer-guide.title') }}
      </h3>
    </v-card-title>
    <v-card-subtitle>
      {{ $t('landingpage.customer-guide.subtitle') }}
    </v-card-subtitle>
    <v-card-text class="px-4">
      <v-alert
        v-if="replacementStatusText"
        :type="replacementAlertType"
        variant="tonal"
        class="mb-4"
      >
        {{ replacementStatusText }}
      </v-alert>
      <v-row>
        <v-col
          cols="12"
          sm="4"
          md="3"
        >
          <MaterialCard
            :name="$t('landingpage.customer-guide.title')"
            :url="customerGuide.publishedFile.cdnUrl"
            :thumbnail-url="customerGuide.previewFile.cdnUrl"
            @download="$tracking.event('Landingpages', 'Clicked', 'Download Guide')"
          />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        v-if="customerGuide.canRegenerate"
        color="primary"
        :loading="isRequestingRegeneration"
        :disabled="isRequestingRegeneration"
        @click="requestRegeneration"
      >
        {{ $t('landingpage.customer-guide.refresh') }}
      </v-btn>
      <v-btn
        v-if="customerGuide.canEdit"
        variant="text"
        :disabled="isRequestingRegeneration"
        @click="isEditingGuide = true"
      >
        {{ $t('landingpage.customer-guide.editor.edit') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import ConfirmationDialog from '@/components/ConfirmationDialog.vue'
import bus, { eventNames } from '@/lib/eventBus'
import MaterialCard from '@/modules/landingpages/MaterialCard.vue'

import CUSTOMER_GUIDE from './CustomerGuide.gql'
import GuideEditor from './GuideEditor.vue'
import REQUEST_GUIDE_REGENERATION from './RequestGuideRegeneration.gql'

const REPLACEMENT_STATUS = {
  GENERATING: 'generating',
  AWAITING_INTERNAL_APPROVAL: 'awaiting-review',
  FAILED: 'failed',
}

export default {
  components: { ConfirmationDialog, GuideEditor, MaterialCard },
  emits: ['draft-dirty'],
  props: {
    guideMaterialId: { type: String, required: true },
  },
  data() {
    return {
      customerGuide: null,
      isRequestingRegeneration: false,
      isEditingGuide: false,
    }
  },
  apollo: {
    customerGuide: {
      query: CUSTOMER_GUIDE,
      variables() {
        return { materialId: this.guideMaterialId }
      },
      skip() {
        return !this.guideMaterialId
      },
      pollInterval() {
        return this.isReplacementInProgress ? 10000 : 0
      },
    },
  },
  computed: {
    isReplacementInProgress() {
      return ['GENERATING', 'AWAITING_INTERNAL_APPROVAL'].includes(
        this.customerGuide?.replacementState
      )
    },
    replacementStatusKey() {
      return REPLACEMENT_STATUS[this.customerGuide?.replacementState] || ''
    },
    replacementStatusText() {
      return this.replacementStatusKey
        ? this.$t(`landingpage.customer-guide.status.${this.replacementStatusKey}`)
        : ''
    },
    replacementAlertType() {
      return this.customerGuide?.replacementState === 'FAILED' ? 'error' : 'info'
    },
  },
  activated() {
    this.refetchGuide()
  },
  methods: {
    async requestRegeneration() {
      const confirmed = await this.$refs.regenerationConfirmation?.confirm()
      if (!confirmed) return

      this.isRequestingRegeneration = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: REQUEST_GUIDE_REGENERATION,
          variables: { materialId: this.guideMaterialId },
        })
        this.customerGuide = data.requestGuideRegeneration
        this.$tracking.event('Landingpages', 'Clicked', 'Refresh Guide')
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('landingpage.customer-guide.queued'),
        })
      } catch (error) {
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('landingpage.customer-guide.error'),
        })
      } finally {
        this.isRequestingRegeneration = false
      }
    },
    refetchGuide() {
      return this.$apollo.queries.customerGuide?.refetch()
    },
    closeGuideEditor() {
      this.isEditingGuide = false
      this.$emit('draft-dirty', false)
    },
    handleDraftSubmission(guide) {
      this.customerGuide = guide
      this.closeGuideEditor()
    },
  },
}
</script>
